/**
 * Scenario Session Inner Component
 * 
 * The core session orchestrator. This is app-agnostic: all app-specific
 * services (auth, API, tracking) are injected via SessionServicesContext.
 * 
 * Each app wraps this with a SessionServicesProvider that provides
 * its own implementations.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAndroidChrome, ENABLE_SYNCED_TEXT_REVEAL } from '@selna/session';
import ReactMarkdown from 'react-markdown';

// Package-internal imports
import type { EmotionType } from '../types/emotion';
import type { SessionResults } from '../context/SessionServicesContext';
import { useSessionServices } from '../context/SessionServicesContext';
import { logger } from '../utils/logger';
import { getLocalizedCharacterName } from '../utils/characterName';
import { createIdentityAssertionTool, LANGUAGE_REMINDERS } from '../config/identityTools';

// Components (from this package)
import LoadingState from './LoadingState';
import ErrorState from './ErrorState';
import SessionHeader from './SessionHeader';
import SessionReadyOverlay from './SessionReadyOverlay';
import VolumeCheckOverlay from './VolumeCheckOverlay';
import WaitingForAIOverlay from './WaitingForAIOverlay';
import ConnectionLostOverlay from './ConnectionLostOverlay';
import ReconnectingOverlay from './ReconnectingOverlay';
import TranscriptOverlay from './TranscriptOverlay';
import SessionControlButtons from './SessionControlButtons';
import KeyboardInput from './KeyboardInput';
import InputModeToggle from './InputModeToggle';
import HintPauseOverlay from './HintPauseOverlay';
import PauseOverlay from './PauseOverlay';
import ExitConfirmDialog from './ExitConfirmDialog';
import HangupConfirmDialog from './HangupConfirmDialog';
import InvalidTranscriptOverlay from './InvalidTranscriptOverlay';
import ConnectionDroppedScreen from './ConnectionDroppedScreen';
import AvatarDisplay from './AvatarDisplay';
import GuidedHintToast from './GuidedHintToast';
import HintSuccessToast from './HintSuccessToast';
import InfoToast from './InfoToast';
import AudioDiagnosticsOverlay from './AudioDiagnosticsOverlay';
import NetworkQualityPill from './NetworkQualityPill';

// Hooks (from this package)
import { useSessionTimer } from '../hooks/useSessionTimer';
import { useTranscriptScroll } from '../hooks/useTranscriptScroll';
import { useAIGreeting } from '../hooks/useAIGreeting';
import { useGuidedHints } from '../hooks/useGuidedHints';
import { useDetailedHelp } from '../hooks/useDetailedHelp';
import { useSessionLifecycle } from '../hooks/useSessionLifecycle';
import { useTranscriptValidation } from '../hooks/useTranscriptValidation';
import { useEmotionDetection } from '../hooks/useEmotionDetection';
import { useEmotionSequence } from '../hooks/useEmotionSequence';
import { useWakeLock } from '../hooks/useWakeLock';
import { useAnalysisMode } from '../hooks/useAnalysisMode';
import { useIdleNudge } from '../hooks/useIdleNudge';
import { useSessionWebSocket } from '../hooks/useSessionWebSocket';
import { useSyncedTextReveal } from '../hooks/useSyncedTextReveal';

// ============================================================================
// Type Definitions
// ============================================================================

export interface ScenarioSessionProps {
  // Option 1: Provide scenarioId (will load from backend)
  scenarioId?: string;

  // Session tracking ID - MUST be captured at component mount time to prevent
  // race conditions where callbacks from old WebSocket connections log to new sessions
  sessionTrackingId?: string;

  // Option 2: Provide pre-loaded scenario data (for deployment flow)
  scenarioData?: {
    id?: string;
    isGameMode?: boolean;
    title?: string;
    description?: string;
    objectives?: string[];
    prompt?: string;
    avatarImages?: Record<EmotionType, string>;
    category?: string;
    industry?: string;
    estimatedTime?: string;
    frontendSituation?: any;
    personaId?: string;
    voice?: string; // Voice for AI avatar
    userReferenceInfo?: string; // User reference information (only visible to user)
    [key: string]: any;
  };
  
  // Skip intro modal if already shown (deployment flow)
  skipIntro?: boolean;
  
  // Initial input mode (ptt, voice, or keyboard)
  initialInputMode?: 'voice' | 'keyboard' | 'ptt';

  // Called when the user explicitly changes the input mode (for persisting preference)
  onInputModeChange?: (mode: 'voice' | 'keyboard' | 'ptt') => void;

  userId?: string;
  evaluationConfig?: any; // Evaluation configuration including guided mode settings
  onFinish: (results: SessionResults) => void;
  onCancel?: () => void;
  // Session input mode: if false, trainees can only use voice (no keyboard/text input)
  allowKeyboardInput?: boolean;
  isDemoMode?: boolean;
  
  // Save/Restore: Initial transcript to restore from a saved session
  initialTranscript?: Array<{ text: string; timestamp: number; speaker: 'user' | 'ai'; id?: string }>;
  // Save callback: called when user clicks "Save & Exit" in PauseOverlay
  onSave?: (results: SessionResults) => Promise<void>;
  // Whether a save operation is in progress
  isSaving?: boolean;
  // Called when the connection drops mid-session (before auto-save).
  // Allows the host to show an appropriate warning toast.
  onConnectionDrop?: () => void;
  // Auto-save callback for ConnectionDroppedScreen: saves the session WITHOUT navigating away.
  // The screen stays visible until the user explicitly clicks "Go Back" (onSaveAndExit).
  // If not provided, falls back to onSave (which saves AND navigates).
  onAutoSave?: (results: SessionResults) => Promise<void>;
  // Exit confirmation: controlled externally (e.g. by browser back button interception)
  showExitConfirmation?: boolean;
  onExitConfirmationDismiss?: () => void;
  // Request to show exit confirmation dialog (e.g. when user clicks back button in SessionHeader)
  onRequestExitConfirmation?: () => void;
  // Optional: called when user clicks "Restart scenario" from poor-audio overlay.
  // If provided, the host can reload without triggering beforeunload (e.g. game).
  onRestartScenario?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export default function ScenarioSessionInner({
  scenarioId,
  sessionTrackingId,
  scenarioData: preLoadedScenarioData,
  skipIntro = false,
  initialInputMode = 'ptt',
  onInputModeChange,
  userId,
  evaluationConfig,
  onFinish,
  onCancel,
  allowKeyboardInput = true,
  isDemoMode = false,
  initialTranscript,
  onSave,
  isSaving = false,
  showExitConfirmation = false,
  onExitConfirmationDismiss,
  onRequestExitConfirmation,
  onRestartScenario,
  onConnectionDrop,
  onAutoSave,
}: ScenarioSessionProps) {
  // =========================================================================
  // Services from context
  // =========================================================================
  const services = useSessionServices();

  // =========================================================================
  // Translation
  // =========================================================================
  const { t, i18n } = useTranslation();
  
  // =========================================================================
  // WebSocket Session Hook
  // =========================================================================
  
  // Initialize WebSocket session with default config
  // Voice will be updated when scenario data loads
  const websocketSession = useSessionWebSocket({
    autoConnect: false, // We'll connect manually after user clicks "Start"
    autoStartRecording: false, // CRITICAL: Mic starts PAUSED - only activates AFTER avatar finishes greeting
    retryOnError: true,
    // Prevent infinite retry loops that look like "spinner forever"
    maxRetryAttempts: 3,
    retryBaseDelayMs: 1500,
    // CRITICAL: Pass sessionTrackingId to prevent race conditions where callbacks
    // from old WebSocket connections log to new sessions (fixes session mixing bug)
    sessionTrackingId: sessionTrackingId
  });
  
  // Ref to track input mode reliably for async callbacks (avoids stale closure issues)
  const inputModeRef = useRef(websocketSession.state.inputMode);
  inputModeRef.current = websocketSession.state.inputMode;

  // 🎯 Text-audio sync: Throttle AI text reveal to match audio playback position (karaoke effect).
  // Instead of showing the full accumulated text instantly, reveals text proportionally to
  // how far audio playback has progressed. Pause-safe (accumulated time freezes during pause).
  const syncedAIText = useSyncedTextReveal({
    fullText: websocketSession.state.aiResponseInProgress,
    getAudioElapsedMs: websocketSession.getAudioElapsedMs,
    getAudioTotalMs: websocketSession.getAudioTotalMs,
    isAudioPlaying: websocketSession.state.isPlaying,
    isEnabled: ENABLE_SYNCED_TEXT_REVEAL,
  });

  // AI Greeting - Manages mic activation when avatar finishes greeting
  const { waitingForAIGreeting, setWaitingForAIGreeting, isFirstGreeting } = useAIGreeting(
    websocketSession.state.isAISpeaking,
    websocketSession.state.aiResponseInProgress,
    websocketSession.state.isPlaying, // AudioManager playback state (handles pauses correctly)
    () => {
      // Callback when greeting ends
      logger.debug('[GREETING] Greeting finished');
    },
    async () => {
      // CRITICAL: Enable turn_detection AND microphone ONLY after avatar finishes greeting
      // This prevents "response already in progress" errors when user speaks too early
      // 
      // 🔧 FIX: Read from ref instead of websocketSession.state.inputMode to avoid
      // stale closure where the callback sees the old 'voice' value instead of 'ptt'.
      const currentInputMode = inputModeRef.current;
      logger.info('[GREETING] onEnableMicrophoneAfterWarmup - currentInputMode from ref:', currentInputMode);

      try {
        if (currentInputMode === 'voice') {
          // VAD mode: Enable semantic_vad turn_detection
          // 🚨 GA API (gpt-realtime-2): turn_detection lives inside audio.input (not at session top level)
          logger.debug('[GREETING] Enabling turn_detection (semantic_vad) for voice mode...');
          await websocketSession.updateSession({
            type: 'realtime',
            audio: {
              input: {
                turn_detection: {
                  type: "semantic_vad",
                  eagerness: "high"
                }
              }
            }
          });
          logger.debug('[GREETING] turn_detection enabled (semantic_vad)');
        } else if (currentInputMode === 'ptt') {
          // PTT mode: Keep turn_detection null (user controls start/stop manually)
          logger.debug('[GREETING] PTT mode - turn_detection stays null');
        }
        // keyboard mode: turn_detection stays null

        // START microphone (first activation after connection)
        // In PTT mode, setInputMode handles mic setup + PTT mode flag
        // await ensures the mic is fully ready before the greeting hook unlocks the UI
        if (currentInputMode === 'ptt') {
          logger.debug('[GREETING] Starting microphone in PTT mode after greeting finished');
          await websocketSession.setInputMode('ptt', { enableMicrophone: true });
        } else if (currentInputMode === 'voice') {
          logger.debug('[GREETING] Starting microphone for first time after greeting finished (voice mode)');
          websocketSession.startRecording();
        } else {
          logger.debug('[GREETING] Keyboard mode - microphone stays paused');
        }
      } catch (error) {
        console.error('[GREETING] Failed to enable turn_detection:', error);
        // Still try to enable microphone even if turn_detection update fails
        // But only if we're in a voice mode, not keyboard
        const fallbackMode = inputModeRef.current;
        if (fallbackMode === 'ptt') {
          await websocketSession.setInputMode('ptt', { enableMicrophone: true });
        } else if (fallbackMode === 'voice') {
          websocketSession.startRecording();
        }
      }
    }
  );
  
  const identityFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // User Reference Info Modal
  const [showReferenceModal, setShowReferenceModal] = useState(false);
  // Badge/glow shown once per scenario attempt (resets each session)
  const [hasClickedReferenceButton, setHasClickedReferenceButton] = useState(false);
  
  // Android Chrome Volume Check - prompt user to adjust media volume before getUserMedia
  const [showVolumeCheck, setShowVolumeCheck] = useState(false);
  const [pendingInputMode, setPendingInputMode] = useState<'voice' | 'keyboard' | 'ptt'>('ptt');
  
  // Info button pulse tracking (max 3 times, persisted in localStorage)
  const INFO_BUTTON_PULSE_KEY = 'infoButtonPulseCount';
  const MAX_PULSE_COUNT = 3;
  
  const getInfoButtonPulseCount = (): number => {
    try {
      const count = localStorage.getItem(INFO_BUTTON_PULSE_KEY);
      return count ? parseInt(count, 10) : 0;
    } catch {
      return 0;
    }
  };
  
  const shouldShowPulse = (): boolean => {
    return getInfoButtonPulseCount() < MAX_PULSE_COUNT;
  };
  
  const incrementPulseCount = (): void => {
    try {
      const currentCount = getInfoButtonPulseCount();
      if (currentCount < MAX_PULSE_COUNT) {
        localStorage.setItem(INFO_BUTTON_PULSE_KEY, String(currentCount + 1));
      }
    } catch {
      // Ignore localStorage errors
    }
  };
  
  // Track pulse display when button becomes visible
  const [hasShownPulseThisSession, setHasShownPulseThisSession] = useState(false);
  
  // Transcript Debug Modal
  const [showTranscriptModal, setShowTranscriptModal] = useState(false);
  
  // Auto-ending state (when AI calls end_conversation in demo)
  const [isAutoEnding, setIsAutoEnding] = useState(false);
  
  // Mid-session connection drop: tracks whether the error happened during an active conversation
  const [isMidSessionDrop, setIsMidSessionDrop] = useState(false);
  
  // Session Lifecycle (extracted to hook)
  const guidedModeEnabledRef = useRef(true);
  const warningsShownRef = useRef(0);
  const hintsProvidedRef = useRef(0);
  const hintsFollowedRef = useRef(0);
  const helpsUsedRef = useRef(0);
  
  const {
    sessionData,
    isLoading,
    loadingProgress,
    error,
    sessionStatus,
    sessionStartTime,
    setSessionStartTime,
    emotionHistory,
    startSession,
    endSession,
    handleCancel,
    isEnding,
    isCancelling,
    setError,
    setSessionStatus,
    setEmotionHistory
  } = useSessionLifecycle({
    scenarioId,
    preLoadedScenarioData,
    skipIntro,
    initialInputMode,
    allowKeyboardInput,
    isDemoMode,
    userId,
    websocketSession,
    onFinish,
    onCancel,
    sessionTrackingId,
    getGuidedHelpStats: () => ({
      enabled: guidedModeEnabledRef.current,
      warningsShown: warningsShownRef.current,
      hintsShown: hintsProvidedRef.current,
      hintsFollowed: hintsFollowedRef.current,
      detailedHelpsUsed: helpsUsedRef.current
    }),
    onWaitingForAIGreetingChange: setWaitingForAIGreeting,
    onEmotionHistoryChange: () => {
      // This callback is called from the hook when emotionHistory changes
    },
    initialTranscript
  });

  // =========================================================================
  // Ensure "ErrorState" is terminal (no background audio / no hidden reconnect)
  // =========================================================================
  const lastHandledErrorRef = useRef<Error | null>(null);
  useEffect(() => {
    if (!error) {
      lastHandledErrorRef.current = null;
      return;
    }
    if (lastHandledErrorRef.current === error) return;
    lastHandledErrorRef.current = error;

    try {
      websocketSession.pausePlayback();
      websocketSession.pauseMicrophone();
      void websocketSession.disconnect();
    } catch (e) {
      console.warn('[ErrorState] Failed to fully stop WebSocket/audio after error:', e);
    }
  }, [error, websocketSession]);

  // =========================================================================
  // Analysis Mode - Westworld-style debugging
  // =========================================================================
  
  const analysisMode = useAnalysisMode({
    scenarioId: scenarioId || sessionData?.scenario?.id || '',
    scenarioData: sessionData?.scenario as any,
    getCurrentPrompt: () => websocketSession.getCurrentInstructions(),
    onPromptReady: async (prompt) => {
      logger.debug('[ANALYSIS] Switching to Analysis Mode prompt');
      await websocketSession.updateSession({
        type: 'realtime',
        instructions: prompt,
        tools: analysisMode.getTools(),
        tool_choice: 'auto',
      });
      websocketSession.enterAnalysisMode();
    },
    onNewPromptAvailable: async (prompt) => {
      logger.debug('[ANALYSIS] Switching back to scenario prompt');
      
      // Build restored tools array from services config
      // Identity assertion tool is ALWAYS included (prevents role confusion)
      const scenarioAiRole = (sessionData?.scenario as any)?.aiRole;
      const scenarioUserRole = (sessionData?.scenario as any)?.userRole;
      const currentLanguage = services.getCurrentLanguage();
      // No fallback (AI_INSTRUCTIONS): the identity tool's role+language enums anchor
      // against dialect drift; if the scenario lacks the roles, fail loud rather than
      // degrade to a free-text tool that lets Brazilian PT through.
      if (!scenarioAiRole || !scenarioUserRole) {
        throw new Error('[ScenarioSessionInner] FATAL: scenario.aiRole and scenario.userRole are REQUIRED to build the identity assertion tool.');
      }
      const identityTool = createIdentityAssertionTool(scenarioAiRole, scenarioUserRole, currentLanguage);
      const restoredTools = [identityTool];
      if (services.config.analysisModeTools && services.config.analysisModeTools.length > 0) {
        restoredTools.push(...services.config.analysisModeTools);
      }
      if (isDemoMode && services.config.conversationEndTool) {
        restoredTools.push(services.config.conversationEndTool);
      }
      
      await websocketSession.updateSession({
        type: 'realtime',
        instructions: prompt,
        tools: restoredTools,
        // Restore the scenario-mode session-level tool_choice. Mirrors the initial
        // setting in useSessionLifecycle. 'required' forces the model to call a tool
        // before every response. The forced-function form {type:'function',name:'...'}
        // was tried but caused the model to fallback to text output instead of audio.
        tool_choice: restoredTools.length > 0
          ? 'required'
          : 'none',
      });
      websocketSession.exitAnalysisMode();
    },
    sendFunctionResult: websocketSession.sendFunctionResult,
  });

  // Pause microphone while Analysis Mode is processing function calls
  const analysisModeProcessingRef = useRef(false);
  
  useEffect(() => {
    const isProcessing = analysisMode.state.isProcessing;
    const wasProcessing = analysisModeProcessingRef.current;
    
    if (isProcessing && !wasProcessing) {
      logger.debug('[ANALYSIS] Pausing microphone during function call processing');
      websocketSession.pauseMicrophone();
      analysisModeProcessingRef.current = true;
    } else if (!isProcessing && wasProcessing) {
      if (websocketSession.state.inputMode === 'voice' || websocketSession.state.inputMode === 'ptt') {
        logger.debug('[ANALYSIS] Resuming microphone after function call processing');
        websocketSession.resumeMicrophone();
      }
      analysisModeProcessingRef.current = false;
    }
  }, [analysisMode.state.isProcessing, websocketSession]);

  // Configure Analysis Mode callbacks using refs to avoid excessive re-registrations
  const analysisModeEnterRef = useRef(analysisMode.enter);
  const analysisModeExitRef = useRef(analysisMode.exit);
  const analysisModeHandleFunctionCallRef = useRef(analysisMode.handleFunctionCall);
  
  useEffect(() => {
    analysisModeEnterRef.current = analysisMode.enter;
    analysisModeExitRef.current = analysisMode.exit;
    analysisModeHandleFunctionCallRef.current = analysisMode.handleFunctionCall;
  });
  
  // Register callbacks once
  useEffect(() => {
    websocketSession.setAnalysisModeCallbacks({
      onEnter: async (messages) => {
        logger.debug('[ANALYSIS] Enter trigger detected');
        try {
          if (!analysisModeEnterRef.current) {
            console.error('[ANALYSIS] analysisModeEnterRef.current is null in onEnter');
            return;
          }
          await analysisModeEnterRef.current(messages);
        } catch (error) {
          console.error('[ANALYSIS] Error in onEnter callback:', error);
        }
      },
      onExit: async () => {
        logger.debug('[ANALYSIS] Exit trigger detected');
        try {
          if (!analysisModeExitRef.current) {
            console.error('[ANALYSIS] analysisModeExitRef.current is null in onExit');
            return;
          }
          await analysisModeExitRef.current();
        } catch (error) {
          console.error('[ANALYSIS] Error in onExit callback:', error);
        }
      },
      onFunctionCall: async (callId, name, args) => {
        logger.debug('[FUNCTION_CALL] Function call received:', name);
        
        try {
          if (name === 'change_language') {
            const { languageCode } = args;
            if (services.updateLanguage) {
              services.updateLanguage(languageCode);
              websocketSession.sendFunctionResult(callId, {
                success: true,
                message: `Language successfully changed to ${languageCode}. The new language instructions have been loaded.`
              });
            } else {
              websocketSession.sendFunctionResult(callId, {
                success: false,
                message: `Failed to change language to ${languageCode}. No update handler is configured.`
              });
            }
            return;
          } else if (name === 'assert_identity') {
            // 🔒 IDENTITY ASSERTION: Validate the AI's declared role against scenario data
            const scenarioAiRole = (sessionData?.scenario as any)?.aiRole || (preLoadedScenarioData as any)?.aiRole;
            const scenarioUserRole = (sessionData?.scenario as any)?.userRole || (preLoadedScenarioData as any)?.userRole;
            const declaredMyRole = args.my_role || '';
            const declaredTheirRole = args.their_role || '';
            
            logger.debug('[IDENTITY] Assert identity:', { 
              declaredMyRole, declaredTheirRole, 
              scenarioAiRole, scenarioUserRole
            });
            
            const currentLanguage = services.getCurrentLanguage();
            const languageReminder = LANGUAGE_REMINDERS[currentLanguage];

            if (identityFlashTimerRef.current) clearTimeout(identityFlashTimerRef.current);

            // 🔒 Send response.create with tool_choice:'auto' for the assert_identity
            // follow-up. The model processes the function output (including
            // language_reminder) before generating audio — critical for dialect
            // priming (PT-PT vs PT-BR) — AND stays free to act on what the user
            // actually said in this same turn: speak normally, OR call
            // enter_analysis_mode (if the user gave the passphrase), OR
            // change_language (if the user asked to switch).
            //
            // Why 'auto' and not 'none': session-level tool_choice:'required'
            // forces a tool call at the START of each user turn (the identity
            // ritual). This follow-up response.create carries an explicit override,
            // so it is NOT forced into another tool call — no infinite loop. The
            // previous 'none' was too blunt: it forbade ALL tools here, which meant
            // that once the ritual ran first (almost always), enter_analysis_mode
            // could never fire and the model fell into the prompt's "Denied." branch.
            // See docs/REALTIME_API_GA_MIGRATION.md (function-calling flow).
            //
            // CRITICAL: Send as plain text string, NOT as JSON object.
            // JSON fields (e.g. { language_reminder: "..." }) are treated as structured data
            // and may be ignored. Plain text is processed as a direct instruction.
            if (scenarioAiRole && scenarioUserRole) {
              const myRoleCorrect = declaredMyRole.toLowerCase() === scenarioAiRole.toLowerCase();

              if (myRoleCorrect) {
                const output = languageReminder
                  ? `Role confirmed: ${scenarioAiRole}. ${languageReminder}`
                  : `Role confirmed: ${scenarioAiRole}.`;
                websocketSession.sendFunctionResult(callId, output, true, 'auto');
              } else {
                logger.warn('[IDENTITY] Role confusion detected!', {
                  declared: declaredMyRole,
                  expected: scenarioAiRole
                });
                const output = languageReminder
                  ? `CORRECTION: You are ${scenarioAiRole}, not ${declaredMyRole}. The user is ${scenarioUserRole}. ${languageReminder}`
                  : `CORRECTION: You are ${scenarioAiRole}, not ${declaredMyRole}. The user is ${scenarioUserRole}.`;
                websocketSession.sendFunctionResult(callId, output, true, 'auto');
              }
            } else {
              const output = languageReminder
                ? `Role confirmed: ${declaredMyRole}. ${languageReminder}`
                : `Role confirmed: ${declaredMyRole}.`;
              websocketSession.sendFunctionResult(callId, output, true, 'auto');
            }
            
          } else if (name === 'enter_analysis_mode') {
            logger.debug('[ANALYSIS] enter_analysis_mode called - activating Analysis Mode');
            
            if (!analysisModeEnterRef.current) {
              console.error('[ANALYSIS] analysisModeEnterRef.current is null');
              websocketSession.sendFunctionResult(callId, { 
                success: false, 
                message: 'Analysis mode initialization error. Please try again.' 
              });
              return;
            }
            
            // Update session FIRST, wait for session.updated, THEN respond to function call
            await analysisModeEnterRef.current(websocketSession.state.messages);
            
            logger.debug('[ANALYSIS] Session updated - sending function result');
            websocketSession.sendFunctionResult(callId, { 
              success: true, 
              message: 'Entering analysis mode. You can now analyze and configure my behavior.' 
            });
            
          } else if (name === 'end_conversation') {
            logger.debug('[DEMO] end_conversation called - preparing to finish session');
            
            websocketSession.pauseMicrophone();
            setIsAutoEnding(true);
            
            websocketSession.sendFunctionResult(callId, { 
              success: true, 
              message: 'Session ending initiated. The conversation will disconnect after the final statement.' 
            });
            
          } else {
            if (!analysisModeHandleFunctionCallRef.current) {
              console.error('[ANALYSIS] analysisModeHandleFunctionCallRef.current is null');
              websocketSession.sendFunctionResult(callId, { 
                success: false, 
                message: 'Analysis mode not ready. Please try again.' 
              });
              return;
            }
            
            analysisModeHandleFunctionCallRef.current(callId, name, args).catch((error: unknown) => {
              console.error('[ANALYSIS] Async error in handleFunctionCall:', error);
              websocketSession.sendFunctionResult(callId, { 
                success: false, 
                message: 'An error occurred while processing the function call.' 
              });
            });
          }
        } catch (error) {
          console.error('[ANALYSIS] Sync error in onFunctionCall:', error);
          websocketSession.sendFunctionResult(callId, { 
            success: false, 
            message: 'An error occurred while processing the request.' 
          });
        }
      },
    });
  }, [websocketSession.setAnalysisModeCallbacks, websocketSession.sendFunctionResult, websocketSession.state.messages]);

  // =========================================================================
  // Per-turn language re-priming (independent of assert_identity)
  // -------------------------------------------------------------------------
  // tool_choice:'required' forces a tool call each turn but not necessarily
  // assert_identity; on turns the model picks another tool (or skips it), the
  // dialect anchor never fires and PT-BR drifts in. We inject the language
  // reminder as a context item on the user's speech-start edge — it lands
  // before the user's transcribed turn and before semantic_vad auto-creates the
  // response (no race), and triggers no response of its own (turn-taking
  // untouched). This is the tool-independent guarantee the function-result
  // reminder alone could not provide.
  // =========================================================================
  const prevUserSpeakingRef = useRef(false);
  useEffect(() => {
    const speaking = websocketSession.state.isUserSpeaking;
    const rising = speaking && !prevUserSpeakingRef.current;
    prevUserSpeakingRef.current = speaking;
    if (!rising) return;
    const reminder = LANGUAGE_REMINDERS[services.getCurrentLanguage()];
    if (!reminder) return;
    websocketSession.injectContextItem(`[Instrução de sistema, não respondas a esta linha: ${reminder}]`);
  }, [websocketSession.state.isUserSpeaking, websocketSession, services]);

  // =========================================================================
  // Graceful Auto-End Flow (Demo Mode)
  // =========================================================================
  useEffect(() => {
    if (!isAutoEnding) return;

    const isAISpeaking = websocketSession.state.isAISpeaking;
    const isAudioPlaying = websocketSession.state.isPlaying;
    const isAIResponding = websocketSession.state.aiResponseInProgress !== '';

    logger.debug('[DEMO] Monitoring auto-end status:', { isAISpeaking, isAudioPlaying, isAIResponding });

    if (!isAISpeaking && !isAudioPlaying && !isAIResponding) {
      logger.debug('[DEMO] AI has finished its parting statement - terminating session now');
      setIsAutoEnding(false);
      endSession();
    }
  }, [
    isAutoEnding, 
    websocketSession.state.isAISpeaking, 
    websocketSession.state.isPlaying, 
    websocketSession.state.aiResponseInProgress,
    endSession
  ]);

  // =========================================================================
  // Force voice mode if keyboard input is disabled by deployment settings
  // =========================================================================
  const currentInputMode = websocketSession.state.inputMode;
  const setInputMode = websocketSession.setInputMode;
  
  useEffect(() => {
    if (!allowKeyboardInput && currentInputMode === 'keyboard') {
      setInputMode('ptt');
    }
  }, [allowKeyboardInput, currentInputMode, setInputMode]);

  // Wrapper for user-initiated input mode changes: sets mode AND notifies parent
  const handleUserInputModeChange = useCallback((mode: 'voice' | 'keyboard' | 'ptt', options?: { enableMicrophone?: boolean }) => {
    setInputMode(mode, options);
    onInputModeChange?.(mode);
  }, [setInputMode, onInputModeChange]);

  // =========================================================================
  // Anti "infinite spinner" guard
  // =========================================================================
  const normalizeWebSocketErrorForUI = useCallback(
    (wsErr: any) => {
      const isAiServiceUnavailable =
        wsErr?.code === 'AI_SERVICE_UNAVAILABLE' || wsErr?.message === 'AI_SERVICE_UNAVAILABLE';
      const isTokenFetchFailure =
        wsErr?.statusCode === 429 ||
        (typeof wsErr?.message === 'string' && wsErr.message.startsWith('Failed to fetch token:'));
      const isWebSocketDisconnect =
        wsErr?.code === '1006' ||
        wsErr?.code === 1006 ||
        (typeof wsErr?.message === 'string' && wsErr.message.includes('WebSocket closed unexpectedly'));

      const normalizedMessage =
        isAiServiceUnavailable || isTokenFetchFailure || isWebSocketDisconnect
          ? t(
              'errors.aiServiceUnavailable',
              "We couldn't connect to the AI service. This can be caused by a temporary internet issue, a VPN/proxy/firewall blocking the connection, or a temporary service disruption. Please try again."
            )
          : (wsErr?.message || t('errors.sessionStart', 'Failed to start session'));

      const err: any = new Error(normalizedMessage);
      err.source = wsErr?.source || 'api.openai.com';
      err.code = wsErr?.code || wsErr?.message;
      if (wsErr?.statusCode) {
        err.statusCode = wsErr.statusCode;
      }
      return err;
    },
    [t]
  );

  useEffect(() => {
    if (sessionStatus !== 'active' || !waitingForAIGreeting) return;
    if (error) return;

    if (websocketSession.state.error) {
      setWaitingForAIGreeting(false);
      setSessionStatus('loading');
      setError(normalizeWebSocketErrorForUI(websocketSession.state.error));
      return;
    }

    const timeoutMs = 20000;
    const timer = setTimeout(() => {
      if (sessionStatus === 'active' && waitingForAIGreeting) {
        const err = new Error(
          t(
            'errors.aiServiceUnavailable',
            "We couldn't connect to the AI service. This can be caused by a temporary internet issue, a VPN/proxy/firewall blocking the connection, or a temporary service disruption. Please try again."
          )
        ) as any;
        err.source = 'api.openai.com';
        setWaitingForAIGreeting(false);
        setSessionStatus('loading');
        setError(err);
      }
    }, timeoutMs);

    return () => clearTimeout(timer);
  }, [
    sessionStatus,
    waitingForAIGreeting,
    error,
    websocketSession.state.error,
    setWaitingForAIGreeting,
    setSessionStatus,
    setError,
    t,
    normalizeWebSocketErrorForUI
  ]);

  useEffect(() => {
    if (sessionStatus !== 'active') return;
    if (error) return;
    const wsErr = websocketSession.state.error as any;
    if (!wsErr) return;
    if (waitingForAIGreeting) return;

    // Halt retries and disconnect IMMEDIATELY — don't wait for the separate
    // error-cleanup effect (which only fires on the next render after setError).
    // The one-render gap left the retry timer active, causing state oscillation
    // that remounted ConnectionDroppedScreen and triggered repeated save attempts.
    try {
      websocketSession.pausePlayback();
      websocketSession.pauseMicrophone();
      void websocketSession.disconnect();
    } catch (_) { /* best-effort */ }

    const hasConversation = websocketSession.state.messages.length > 0;
    if (hasConversation && onSave) {
      setIsMidSessionDrop(true);
      onConnectionDrop?.();
    }
    setSessionStatus('loading');
    setError(normalizeWebSocketErrorForUI(wsErr));
  }, [
    sessionStatus,
    websocketSession.state.error,
    waitingForAIGreeting,
    error,
    onSave,
    websocketSession.state.messages.length,
    setSessionStatus,
    setError,
    normalizeWebSocketErrorForUI,
    websocketSession,
  ]);

  // Get localized character name
  const localizedCharacterName = getLocalizedCharacterName(
    preLoadedScenarioData?.characterName || sessionData?.scenario?.characterName,
    i18n.language
  );

  // Track pulse display when button becomes visible
  useEffect(() => {
    if (sessionStatus !== 'active') {
      setHasShownPulseThisSession(false);
      return;
    }
    
    if ((sessionData?.scenario as any)?.userReferenceInfo && 
        sessionStatus === 'active' && 
        !showReferenceModal && 
        !hasShownPulseThisSession &&
        shouldShowPulse()) {
      incrementPulseCount();
      setHasShownPulseThisSession(true);
    }
  }, [sessionStatus, showReferenceModal, sessionData, hasShownPulseThisSession]);

  useEffect(() => {
    if (sessionData?.scenario) {
      const userRefInfo = (sessionData.scenario as any).userReferenceInfo;
      logger.debug('[USER_REFERENCE_INFO] SessionData loaded:', {
        hasUserReferenceInfo: Boolean(userRefInfo),
        scenarioKeysCount: Object.keys(sessionData.scenario).length
      });
    }
  }, [sessionData]);

  // Wake Lock - Keep screen on during active session
  useWakeLock({
    enabled: sessionStatus === 'active',
    onVisibilityChange: (isVisible) => {
      logger.debug(`[Wake Lock] Visibility changed: ${isVisible ? 'VISIBLE' : 'BACKGROUND'}`);
    },
    onStateChange: (state) => {
      logger.debug(`[Wake Lock] State changed: ${state}`);
    }
  });

  // Manual Pause State
  const [isUserPaused, setIsUserPaused] = useState<boolean>(false);
  const [isPauseWaiting, setIsPauseWaiting] = useState<boolean>(false);
  
  // Keyboard mode fallback notification
  const [showMicFallbackToast, setShowMicFallbackToast] = useState<boolean>(false);

  // ⏳ Idle Nudge — prompts avatar to check on silent user in PTT mode
  const handleIdleNudge = useCallback(() => {
    websocketSession.sendSystemMessage(
      'SYSTEM: The user has been silent for several seconds after your last message. ' +
      'Briefly and naturally check if they are still there or gently prompt a response. ' +
      'Stay completely in character. Maximum 1 sentence.'
    );
  }, [websocketSession.sendSystemMessage]);

  useIdleNudge({
    isPTTMode: websocketSession.state.inputMode === 'ptt',
    isGreetingActive: waitingForAIGreeting || isFirstGreeting,
    isUserSpeaking: websocketSession.state.isUserSpeaking,
    aiResponseInProgress: !!websocketSession.state.aiResponseInProgress,
    isAudioPlaying: websocketSession.state.isPlaying,
    isPaused: isUserPaused,
    isAnalysisMode: !!websocketSession.state.analysisMode,
    onNudge: handleIdleNudge,
  });

  // 🎙️ PTT Spacebar handler (desktop only)
  // Hold Space to talk, release to send. Only active in PTT mode.
  const spacebarPressedRef = useRef(false);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle Space in PTT mode, not when typing in inputs
      if (e.code !== 'Space' || e.repeat) return;
      if (websocketSession.state.inputMode !== 'ptt') return;
      
      // 🔧 FIX: Block PTT during greeting — mic isn't ready yet.
      // Without this guard, user could press Space during/right after greeting,
      // creating phantom placeholders that cause message ordering issues.
      if (waitingForAIGreeting || isFirstGreeting) return;
      
      // 🔬 Block PTT only while Analysis Mode is PROCESSING a function call — that's the
      // brief window where the mic is paused (see analysisModeProcessingRef above). The
      // rest of Analysis Mode is a normal interactive turn, so spacebar PTT must work,
      // matching the PTT button (which gates on isAnalysisModeProcessing, not the whole
      // mode). The previous guard blocked the ENTIRE mode, so the spacebar was dead in
      // Analysis Mode while clicking the button still worked. Ref read = always fresh.
      if (analysisModeProcessingRef.current) return;
      
      // Don't intercept if user is typing in a text input/textarea
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      
      e.preventDefault();
      if (!spacebarPressedRef.current) {
        spacebarPressedRef.current = true;
        websocketSession.handlePTTPressed();
      }
    };
    
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      if (websocketSession.state.inputMode !== 'ptt') return;
      
      e.preventDefault();
      if (spacebarPressedRef.current) {
        spacebarPressedRef.current = false;
        websocketSession.handlePTTReleased();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [websocketSession.state.inputMode, websocketSession.handlePTTPressed, websocketSession.handlePTTReleased]);
  
  // Session Timer - pauses when user manually pauses
  const { duration: sessionDuration, formattedDuration } = useSessionTimer(
    sessionStatus === 'active',
    sessionStartTime,
    isUserPaused
  );
  
  // Pending Transcript
  const showPendingBubble = websocketSession.state.isUserSpeaking;
  
  // Guided Hints
  const maxHints = evaluationConfig?.maxGuidedHints ?? 5;
  const { 
    hints, 
    activeHint, 
    dismissActiveHint, 
    warningsShown, 
    hintsShown,
    hintsFollowed,
    successHint,
    dismissSuccessHint,
    guidedModeEnabled,
  } = useGuidedHints({
    enabled: true,
    maxHints,
    sessionStatus,
    aiTextResponseComplete: websocketSession.state.aiTextResponseComplete,
    isPaused: isUserPaused,
    isAnalysisMode: websocketSession.state.analysisMode,
    isUserSpeaking: websocketSession.state.isRecording,
    aiResponseInProgress: websocketSession.state.aiResponseInProgress,
    messages: websocketSession.state.messages,
    sessionData,
    sessionStartTime,
    sessionDuration,
    userId,
    emotionHistory,
    credentials: services.credentials
  });

  // Detailed Help
  const maxDetailedHelps = 3;
  const { 
    helpsUsed, 
    currentHelp, 
    dismissHelp
  } = useDetailedHelp({
    maxHelps: maxDetailedHelps,
    sessionStatus,
    messages: websocketSession.state.messages,
    sessionData,
    sessionStartTime,
    sessionDuration,
    userId,
    emotionHistory
  });

  // Update guided help stats refs for session lifecycle
  useEffect(() => {
    guidedModeEnabledRef.current = guidedModeEnabled;
    warningsShownRef.current = warningsShown;
    hintsProvidedRef.current = hintsShown;
    hintsFollowedRef.current = hintsFollowed;
    helpsUsedRef.current = helpsUsed;
    
    logger.debug('[Session] Updated guided help stats refs:', {
      guidedModeEnabled,
      warningsShown,
      hintsProvided: hintsShown,
      hintsFollowed,
      helpsUsed
    });
  }, [guidedModeEnabled, warningsShown, hintsShown, hintsFollowed, helpsUsed]);

  // Transcript Validation
  const scenarioTranscriptValidation = (sessionData?.scenario?.transcriptValidationEnabled ?? preLoadedScenarioData?.transcriptValidationEnabled) !== false;
  const { 
    isTranscriptInvalid, 
    invalidReason, 
    isConversationEnding
  } = useTranscriptValidation({
    enabled: sessionStatus === 'active' && scenarioTranscriptValidation,
    sessionStatus,
    messages: websocketSession.state.messages,
    scenarioId: sessionData?.scenario?.id,
    scenarioTitle: sessionData?.scenario?.title,
    initialTranscriptLength: initialTranscript
      ? Math.min(
          initialTranscript.filter(m => m.speaker === 'user').length,
          initialTranscript.filter(m => m.speaker === 'ai').length
        )
      : 0,
  });

  useEffect(() => {
    logger.debug('[FAREWELL_DETECTION] State changed - isConversationEnding:', isConversationEnding);
  }, [isConversationEnding]);

  // Auto-pause session when transcript is invalid
  useEffect(() => {
    if (isTranscriptInvalid && sessionStatus === 'active') {
      logger.info('[Session] Auto-pausing due to invalid transcript (poor audio quality)');
      websocketSession.pauseMicrophone();
      websocketSession.pausePlayback();
    }
  }, [isTranscriptInvalid, sessionStatus, websocketSession]);

  const handleTranscriptInvalidRestart = () => {
    logger.info('[Session] Restarting due to invalid transcript');
    if (onRestartScenario) {
      onRestartScenario();
    } else {
      window.location.reload();
    }
  };

  // 💾 Restore initial transcript into the UI (so user sees previous messages)
  // NOTE: The conversation context for the AI is now injected into the system prompt
  // BEFORE the connection, via useSessionLifecycle. This ensures the avatar already
  // knows about the previous conversation when it sends its first greeting.
  const initialTranscriptLoadedRef = useRef(false);
  useEffect(() => {
    if (initialTranscript && initialTranscript.length > 0 && !initialTranscriptLoadedRef.current && sessionStatus === 'active') {
      initialTranscriptLoadedRef.current = true;
      logger.info('[💾 RESTORE] Loading initial transcript into UI with', initialTranscript.length, 'messages');
      websocketSession.loadTranscript(initialTranscript);
    }
  }, [initialTranscript, sessionStatus, websocketSession.loadTranscript]);

  // 💾 Handle save from PauseOverlay
  const handleSaveFromPause = useCallback(async () => {
    if (!onSave) return;
    
    // Build a minimal SessionResults for the save callback
    const results: SessionResults = {
      sessionId: sessionTrackingId || '',
      scenarioId: scenarioId || sessionData?.scenario?.id || '',
      userId: userId || '',
      tenantId: '',
      startedAt: sessionStartTime || new Date(),
      endedAt: new Date(),
      duration: sessionDuration,
      status: 'abandoned',
      transcript: websocketSession.state.messages.map(m => ({
        timestamp: m.timestamp,
        speaker: m.speaker,
        text: m.text,
      })),
      emotionHistory: emotionHistory.map(e => ({
        timestamp: e.timestamp,
        emotion: e.emotion as any,
      })),
      scenario: {
        id: scenarioId || sessionData?.scenario?.id || '',
        title: sessionData?.scenario?.title || '',
        category: sessionData?.scenario?.category || '',
      },
      persona: sessionData?.persona || null,
    };
    
    await onSave(results);
  }, [onSave, websocketSession.state.messages, emotionHistory, sessionDuration, sessionTrackingId, scenarioId, userId, sessionData, sessionStartTime]);

  // 🔙 Exit confirmation dialog handlers (triggered by browser back button or external control)
  const handleExitSave = useCallback(async () => {
    // handleSaveFromPause calls onSave which handles navigation (status → 'ready')
    // No need to call onExitConfirmationDismiss - the parent handles cleanup
    await handleSaveFromPause();
  }, [handleSaveFromPause]);

  const handleExitWithoutSaving = useCallback(() => {
    onExitConfirmationDismiss?.();
    handleCancel();
  }, [onExitConfirmationDismiss, handleCancel]);

  const handleExitCancel = useCallback(() => {
    onExitConfirmationDismiss?.();
  }, [onExitConfirmationDismiss]);

  // 📞 Hangup confirmation: shown when user presses the red end-call button.
  // Prevents accidental clicks from skipping straight to evaluation.
  const [showHangupConfirm, setShowHangupConfirm] = useState(false);

  const handleHangupRequest = useCallback(() => {
    setShowHangupConfirm(true);
  }, []);

  const handleHangupEndAndEvaluate = useCallback(() => {
    setShowHangupConfirm(false);
    endSession();
  }, [endSession]);

  const handleHangupSaveAndExit = useCallback(async () => {
    // Same path as the back-button "save & exit" — onSave handles navigation.
    await handleSaveFromPause();
    setShowHangupConfirm(false);
  }, [handleSaveFromPause]);

  const handleHangupCancel = useCallback(() => {
    setShowHangupConfirm(false);
  }, []);

  // Stable auto-save callback for ConnectionDroppedScreen.
  // Previously created inline inside the render body, so every re-render produced
  // a new function reference → React re-ran the useEffect in ConnectionDroppedScreen
  // (although the ref guard prevented re-execution, the unstable ref combined with
  // the retry-timer race made the save loop possible).
  const autoSaveForDrop = useCallback(async () => {
    if (!onAutoSave) {
      await handleSaveFromPause();
      return;
    }
    const results: SessionResults = {
      sessionId: sessionTrackingId || '',
      scenarioId: scenarioId || sessionData?.scenario?.id || '',
      userId: userId || '',
      tenantId: '',
      startedAt: sessionStartTime || new Date(),
      endedAt: new Date(),
      duration: sessionDuration,
      status: 'abandoned',
      transcript: websocketSession.state.messages.map(m => ({
        timestamp: m.timestamp,
        speaker: m.speaker,
        text: m.text,
      })),
      emotionHistory: emotionHistory.map(e => ({
        timestamp: e.timestamp,
        emotion: e.emotion as any,
      })),
      scenario: {
        id: scenarioId || sessionData?.scenario?.id || '',
        title: sessionData?.scenario?.title || '',
        category: sessionData?.scenario?.category || '',
      },
      persona: sessionData?.persona || null,
    };
    await onAutoSave(results);
  }, [onAutoSave, handleSaveFromPause, sessionTrackingId, scenarioId, userId, sessionData, sessionStartTime, sessionDuration, websocketSession.state.messages, emotionHistory]);

  const isPaused = isUserPaused;
  
  // Transcript Scroll
  const { shouldScrollToBottom: scrollToBottom } = useTranscriptScroll(
    websocketSession.state.messages,
    hints
  );
  
  // Atypical-voice VAD: relax client-side speech detection thresholds for
  // post-laryngectomy / esophageal speech, which the standard band-ratio
  // check rejects as non-voice.
  const atypicalVoiceVAD = !!(
    (sessionData?.scenario as any)?.atypicalVoiceVAD
    ?? (preLoadedScenarioData as any)?.atypicalVoiceVAD
  );
  useEffect(() => {
    websocketSession.setAtypicalVoiceMode(atypicalVoiceVAD);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atypicalVoiceVAD]);

  // Emotion Management
  const positiveExpressionsOnly = !!(preLoadedScenarioData as any)?.positiveExpressionsOnly;
  const rawInitialEmotion = (
    preLoadedScenarioData?.behavioralProfile?.initialEmotion ||
    preLoadedScenarioData?.customPersona?.initialEmotion ||
    'neutral'
  ) as EmotionType;
  const initialEmotion = (() => {
    if (positiveExpressionsOnly) {
      const allowedPositiveEmotions = ['neutral', 'interested', 'encouraging', 'proud', 'amused', 'relieved', 'enthusiastic', 'attentive', 'thinking', 'curious', 'contemplative', 'pensive'];
      if (!allowedPositiveEmotions.includes(rawInitialEmotion)) {
        return 'neutral' as EmotionType;
      }
    }
    return rawInitialEmotion;
  })();
  const emotionSequence = useEmotionSequence(initialEmotion);

  // Show toast when automatically switched to keyboard mode due to mic error
  useEffect(() => {
    if (websocketSession.state.microphoneFallback && !showMicFallbackToast) {
      logger.debug('[FALLBACK] Showing toast notification about keyboard mode');
      setShowMicFallbackToast(true);
    }
  }, [websocketSession.state.microphoneFallback, showMicFallbackToast]);

  // Track paused state changes to pause/resume WebSocket
  const isPausedRef = useRef(false);
  
  // Count exchanges for end session button
  const exchangeCount = Math.min(
    websocketSession.state.messages.filter(m => m.speaker === 'user').length,
    websocketSession.state.messages.filter(m => m.speaker === 'ai').length
  );
  const canEndSession = exchangeCount >= 3;
  
  useEffect(() => {
    if (isPaused && !isPausedRef.current) {
      logger.info('[Pause] Session paused - disabling mic & avatar');
      websocketSession.pauseMicrophone();
      websocketSession.pausePlayback();
      isPausedRef.current = true;
    } else if (!isPaused && isPausedRef.current) {
      logger.info('[Pause] Session resumed - enabling mic & avatar');
      if (websocketSession.state.inputMode === 'voice' || websocketSession.state.inputMode === 'ptt') {
        websocketSession.resumeMicrophone();
      }
      websocketSession.resumePlayback();
      isPausedRef.current = false;
    }
  }, [isPaused, websocketSession]);


  // Pause/Resume Handlers
  const handlePause = () => {
    logger.debug('[Pause] User clicked pause button');
    websocketSession.pauseMicrophone();
    websocketSession.pausePlayback();
    setIsUserPaused(true);
    setIsPauseWaiting(false);
  };

  const handleResume = () => {
    logger.debug('[Pause] User clicked resume button');
    setIsUserPaused(false);
    setIsPauseWaiting(false);
    websocketSession.resumePlayback();
  };

  // =========================================================================
  // Emotion Detection
  // =========================================================================
  useEmotionDetection({
    sessionStatus,
    sessionData: sessionData as any,
    websocketState: websocketSession.state,
    emotionSequence,
    localizedCharacterName,
    setEmotionHistory
  });

  /**
   * Hide overlay when user speaks first
   */
  useEffect(() => {
    const firstSpeaker = sessionData?.scenario.firstSpeaker || 'avatar';
    
    if (firstSpeaker !== 'user' || sessionStatus !== 'active' || !waitingForAIGreeting) {
      return;
    }
    
    logger.debug('[USER FIRST] Starting 3-second countdown + checking system readiness...');
    
    const timer = setTimeout(() => {
      logger.debug('[USER FIRST] 3 seconds passed');
      
      if (websocketSession.state.isRecording) {
        logger.debug('[USER FIRST] Mic is ready - hiding overlay NOW');
        setWaitingForAIGreeting(false);
      } else {
        logger.debug('[USER FIRST] Mic not ready yet, polling...');
        
        const pollInterval = setInterval(() => {
          if (websocketSession.state.isRecording) {
            logger.debug('[USER FIRST] Mic is ready - hiding overlay NOW');
            setWaitingForAIGreeting(false);
            clearInterval(pollInterval);
          }
        }, 100);
        
        setTimeout(() => {
          logger.debug('[USER FIRST] Max polling time - hiding overlay anyway');
          setWaitingForAIGreeting(false);
          clearInterval(pollInterval);
        }, 10000);
      }
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [sessionStatus, waitingForAIGreeting, sessionData, websocketSession.state.isRecording, setWaitingForAIGreeting]);
  
  /**
   * Start timer only when the session actually begins
   */
  useEffect(() => {
    if (sessionStatus !== 'active' || sessionStartTime) return;
    if (!sessionData?.scenario) return;

    const firstSpeaker = sessionData.scenario.firstSpeaker || 'avatar';

    if (firstSpeaker === 'avatar') {
      if (isFirstGreeting || websocketSession.state.isAISpeaking || !!websocketSession.state.aiResponseInProgress) {
        logger.debug('[TIMER] Greeting started - starting session timer NOW');
        setSessionStartTime(new Date());
      }
      return;
    }

    if (!waitingForAIGreeting) {
      logger.debug('[TIMER] User can speak - starting session timer NOW');
      setSessionStartTime(new Date());
    }
  }, [
    sessionStatus,
    sessionStartTime,
    sessionData,
    waitingForAIGreeting,
    isFirstGreeting,
    websocketSession.state.isAISpeaking,
    websocketSession.state.aiResponseInProgress,
    setSessionStartTime
  ]);
  
  // =========================================================================
  // Render
  // =========================================================================
  
   // Loading state
  if (isLoading) {
    return <LoadingState progress={loadingProgress} />;
  }

  // Transition guard: prevent flash of active session UI while endSession is disconnecting
  // isEnding is set synchronously (useState) before the async disconnect + navigate,
  // so it blocks ALL intermediate re-renders triggered by XState connection state changes
  if (isEnding) {
    return <LoadingState progress={null} />;
  }
  
  // Error state
  if (error) {
    if (isMidSessionDrop && onSave) {
      return (
        <ConnectionDroppedScreen
          onReconnect={onRestartScenario || (() => window.location.reload())}
          onSaveAndExit={handleCancel}
          onAutoSave={autoSaveForDrop}
          isSaving={isSaving}
          hasMessages={websocketSession.state.messages.length > 0}
        />
      );
    }

    const handleContinueKeyboard = () => {
      setError(null);
      websocketSession.clearError();
      websocketSession.setInputMode('keyboard');
      
      if (isDemoMode) {
        setSessionStatus('active');
        startSession('keyboard');
      } else {
        setSessionStatus('ready');
      }
    };
    
    return (
      <ErrorState
        error={error}
        onRetry={onRestartScenario || (() => window.location.reload())}
        onCancel={handleCancel}
        allowKeyboardInput={allowKeyboardInput}
        onContinueKeyboard={allowKeyboardInput ? handleContinueKeyboard : undefined}
      />
    );
  }
  
  if (!sessionData) {
    return null;
  }
  
  // Ready/Active/Finished states
  return (
    <>
      {/* Hide scrollbar + Prevent any scrolling + Glow animation */}
      <style>{`
        /* Hide scrollbar */
        .messages-container::-webkit-scrollbar { display: none; }
        
        /* Pulsing ring animation for info button */
        @keyframes pulse-ring {
          0% {
            transform: scale(1);
            opacity: 0.8;
          }
          100% {
            transform: scale(1.8);
            opacity: 0;
          }
        }
        
        .pulse-ring {
          animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
      <div className="fixed left-0 top-0 w-full h-screen-safe bg-black flex flex-col">
      
      {/* Main Content - Avatar */}
      <div className={websocketSession.state.inputMode === 'keyboard' ? 'flex-1 min-h-0 relative' : 'flex-1 relative'}>
        <div className="h-full">
          <AvatarDisplay
            avatarImages={sessionData.avatarImages}
            controlledEmotion={emotionSequence.currentEmotion}
            disableAutoCycle={true}
            className="h-full"
            imageMode={sessionData.scenario?.isGameMode ? 'contain' : 'cover'}
          />
        </div>
        
        {/* Header Overlay */}
        <SessionHeader
          sessionStatus={sessionStatus === 'loading' ? 'ready' : sessionStatus}
          formattedDuration={formattedDuration}
          isAudioPlaying={websocketSession.state.isPlaying}
          getAudioFrequencyData={websocketSession.getAudioFrequencyData}
          onEndSession={handleHangupRequest}
          onPause={handlePause}
          onResume={handleResume}
          isPaused={isUserPaused}
          isPauseWaiting={isPauseWaiting}
          isRecording={websocketSession.state.isRecording}
          isUserSpeaking={websocketSession.state.isUserSpeaking}
          onCancel={onCancel}
          onRequestExitConfirmation={onRequestExitConfirmation}
          shouldGlow={isConversationEnding}
          waitingForAIGreeting={waitingForAIGreeting}
          canEndSession={canEndSession}
          inputMode={websocketSession.state.inputMode}
          onInputModeToggle={handleUserInputModeChange}
          allowKeyboardInput={allowKeyboardInput}
          characterName={localizedCharacterName !== 'Character' ? localizedCharacterName : undefined}
          characterRole={(sessionData?.scenario as any)?.aiRole || (preLoadedScenarioData as any)?.aiRole}
          hideGradient={showReferenceModal}
        />
        

        {/* Analysis Mode Indicator */}
        {websocketSession.state.analysisMode && (
          <div className="absolute top-16 md:top-4 left-1/2 transform -translate-x-1/2 z-40 pointer-events-none">
            <div className="px-4 py-2 rounded-full bg-amber-500/90 text-white text-sm font-medium shadow-lg flex items-center gap-2 animate-pulse">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              Analysis Mode
            </div>
          </div>
        )}
        
        {/* User Reference Info Modal */}
        {showReferenceModal && (sessionData?.scenario as any)?.userReferenceInfo && (
          <div 
            className="fixed top-16 md:top-20 left-0 right-0 bottom-0 z-50 flex items-center justify-center xl:justify-end"
            onClick={() => setShowReferenceModal(false)}
          >
            <div 
              className="bg-white/50 backdrop-blur-xl backdrop-saturate-150 rounded-2xl shadow-2xl border border-white/20
                         w-full mx-4 max-h-[80vh] overflow-y-auto
                         max-w-2xl xl:max-w-md xl:mr-4 xl:my-4 xl:h-[calc(100vh-7rem)]
                         overscroll-contain scrollbar-thin scrollbar-thumb-gray-400/50 scrollbar-track-transparent"
              style={{ WebkitOverflowScrolling: 'touch' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-green-600/90 backdrop-blur-sm text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h2 className="text-xl font-semibold">{t('scenario.session.referenceInfo.title', 'Reference Information')}</h2>
                </div>
                <button
                  onClick={() => setShowReferenceModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* "You can continue speaking" banner */}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mx-4 mt-4">
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                  <p className="text-sm font-medium text-blue-800">
                    {t('scenario.session.referenceInfo.canSpeak', 'You can continue speaking while reading this information')}
                  </p>
                </div>
              </div>
              
              <div className="p-6">
                <div className="prose prose-sm max-w-none text-gray-800">
                  <ReactMarkdown>{(sessionData.scenario as any).userReferenceInfo}</ReactMarkdown>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Transcript Debug Modal */}
        {showTranscriptModal && sessionStatus === 'active' && (
          <div 
            className="fixed top-16 md:top-20 left-0 right-0 bottom-0 z-50 flex items-center justify-center xl:justify-end bg-black/25 md:bg-transparent backdrop-blur-sm md:backdrop-blur-none"
            onClick={() => setShowTranscriptModal(false)}
          >
            <div 
              className="bg-white rounded-lg shadow-2xl w-full mx-4 max-h-[80vh] overflow-y-auto
                         max-w-2xl xl:max-w-md xl:mr-4 xl:my-4 xl:h-[calc(100vh-7rem)]
                         overscroll-contain scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100"
              style={{ WebkitOverflowScrolling: 'touch' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-purple-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h2 className="text-xl font-semibold">Transcript Debug</h2>
                </div>
                <button
                  onClick={() => setShowTranscriptModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mx-4 mt-4">
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                  <p className="text-sm font-medium text-blue-800">
                    You can continue speaking while reading this information
                  </p>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  {websocketSession.state.messages.length === 0 ? (
                    <p className="text-gray-500 italic">No messages yet...</p>
                  ) : (
                    websocketSession.state.messages.map((msg, idx) => (
                      <div key={idx} className="border-b border-gray-200 pb-4 last:border-b-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs font-semibold px-2 py-1 rounded ${
                            msg.speaker === 'user' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                          }`}>
                            {msg.speaker === 'user' ? 'USER' : 'AI'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(msg.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="text-sm text-gray-800 whitespace-pre-wrap mb-2">
                          <strong>Processed:</strong> {msg.text}
                        </div>
                        {msg.rawTranscript && msg.rawTranscript !== msg.text && (
                          <div className="text-xs text-red-600 bg-red-50 p-2 rounded border border-red-200">
                            <strong>Raw (before filtering):</strong> {msg.rawTranscript}
                          </div>
                        )}
                        {msg.rawTranscript && msg.rawTranscript === msg.text && (
                          <div className="text-xs text-gray-500 italic">
                            (Raw transcript same as processed - no filtering applied)
                          </div>
                        )}
                        {msg.speaker === 'user' && !msg.rawTranscript && (
                          <div className="text-xs text-gray-500 italic">
                            (No raw transcript - message from keyboard input)
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Ready state overlay */}
        {sessionStatus === 'ready' && !showVolumeCheck && (
          <SessionReadyOverlay
            title={sessionData.scenario.title}
            description={sessionData.scenario.description || ''}
            objectives={sessionData.scenario.objectives}
            languageMode={sessionData.scenario.languageMode}
            onStart={(inputMode) => {
              if (isAndroidChrome()) {
                logger.debug('[VOLUME CHECK] Android Chrome detected - showing volume check overlay');
                setPendingInputMode(inputMode);
                setShowVolumeCheck(true);
              } else {
                startSession(inputMode);
              }
            }}
            onCancel={handleCancel}
          />
        )}
        
        {/* Android Chrome Volume Check Overlay */}
        {sessionStatus === 'ready' && showVolumeCheck && (
          <VolumeCheckOverlay
            onContinue={() => {
              logger.info('[VOLUME CHECK] User confirmed volume, starting session');
              setShowVolumeCheck(false);
              startSession(pendingInputMode);
            }}
            onBack={() => {
              logger.debug('[VOLUME CHECK] User went back to ready overlay');
              setShowVolumeCheck(false);
            }}
          />
        )}
        
        {/* Waiting for AI overlay */}
        {sessionStatus === 'active' && waitingForAIGreeting && !isFirstGreeting && (
          <WaitingForAIOverlay
            characterName={localizedCharacterName || sessionData.persona?.name || 'Character'}
            firstSpeaker={sessionData.scenario.firstSpeaker || 'avatar'}
          />
        )}

        {/* Reconnecting overlay (mid-session, system is actively retrying) */}
        {sessionStatus === 'active' &&
          !isCancelling &&
          !waitingForAIGreeting &&
          websocketSession.state.connectionStatus === 'reconnecting' && (
            <ReconnectingOverlay
              retryAttempt={websocketSession.state.retryCount}
              maxRetries={3}
              onCancel={() => void websocketSession.disconnect()}
              onSave={onSave ? handleSaveFromPause : undefined}
              isSaving={isSaving}
            />
          )}

        {/* Connection lost overlay (mid-session, NOT during intentional cancel, NOT reconnecting) */}
        {sessionStatus === 'active' &&
          !isCancelling &&
          !waitingForAIGreeting &&
          !websocketSession.state.error &&
          websocketSession.state.connectionStatus !== 'connected' &&
          websocketSession.state.connectionStatus !== 'reconnecting' && (
            <ConnectionLostOverlay onRetry={websocketSession.connect} />
          )}
        
        {/* Audio diagnostics overlay (toggle with Ctrl+Shift+D) */}
        {sessionStatus === 'active' && (
          <AudioDiagnosticsOverlay getDiagnostics={websocketSession.getAudioDiagnostics} />
        )}

        {/* Network-quality pill — surfaces sustained slow-network state to the user */}
        {sessionStatus === 'active' && (
          <NetworkQualityPill getDiagnostics={websocketSession.getAudioDiagnostics} isPaused={isUserPaused} />
        )}

        {/* Transcript overlay */}
        {sessionStatus === 'active' && (
          <TranscriptOverlay
            messages={websocketSession.state.messages}
            hints={hints}
            showPendingBubble={showPendingBubble}
            transcriptInProgress={websocketSession.state.transcriptInProgress}
            aiResponseInProgress={syncedAIText}
            avatarImageUrl={sessionData.avatarImages?.neutral}
            characterName={localizedCharacterName || sessionData.persona?.name || 'Character'}
            scrollToBottom={scrollToBottom}
            getMicrophoneFrequencyData={websocketSession.getMicrophoneFrequencyData}
            isUserSpeaking={websocketSession.state.isUserSpeaking}
            currentTranscriptId={websocketSession.state.currentTranscriptId}
            isKeyboardMode={websocketSession.state.inputMode === 'keyboard'}
            isPTTMode={websocketSession.state.inputMode === 'ptt'}
            isWaitingForTranscript={!!websocketSession.state.userSpeechStoppedTimestamp && !websocketSession.state.aiResponseInProgress}
            onPTTPressed={websocketSession.handlePTTPressed}
            onPTTReleased={websocketSession.handlePTTReleased}
            onPauseMicrophone={websocketSession.pauseMicrophone}
            onResumeMicrophone={websocketSession.resumeMicrophone}
            isAudioPlaying={websocketSession.state.isPlaying}
            isAnalysisModeProcessing={analysisMode.state.isProcessing}
            isPaused={isUserPaused}
            hidePTTGradient={showReferenceModal}
            isPTTReady={!waitingForAIGreeting && !isFirstGreeting}
          />
        )}

        {/* Session Control Buttons - Desktop only */}
        {sessionStatus === 'active' && (
          <div className={`hidden md:flex flex-col items-center gap-3 absolute left-1/2 transform -translate-x-1/2 z-20 pointer-events-auto transition-all duration-300 ${
            websocketSession.state.inputMode === 'keyboard' ? 'bottom-24' : 'bottom-4'
          }`}>
            <div className="flex items-center gap-6">
              <SessionControlButtons
                onPause={handlePause}
                onResume={handleResume}
                onEndSession={handleHangupRequest}
                isPaused={isUserPaused}
                isPauseWaiting={isPauseWaiting}
                shouldGlow={isConversationEnding}
                canEndSession={canEndSession}
                variant="desktop"
              />
            </div>
            
            <div className="flex items-center gap-3">
              {allowKeyboardInput && (
                <InputModeToggle
                  currentMode={websocketSession.state.inputMode}
                  onToggle={(mode) => handleUserInputModeChange(mode, { enableMicrophone: true })}
                  variant="mobile"
                  disabled={waitingForAIGreeting}
                />
              )}
              
              {websocketSession.state.inputMode === 'keyboard' && (
                <button
                  onClick={websocketSession.toggleAvatarAudio}
                  className={`w-10 h-10 rounded-full shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-110 active:scale-95 flex items-center justify-center ${
                    websocketSession.state.avatarAudioEnabled
                      ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                      : 'bg-gray-600 hover:bg-gray-700 active:bg-gray-800'
                  }`}
                  title={websocketSession.state.avatarAudioEnabled ? 'Mute avatar audio' : 'Unmute avatar audio'}
                >
                  {websocketSession.state.avatarAudioEnabled ? (
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Warning Overlay */}
        {activeHint && activeHint.type === 'warning' && (
          <HintPauseOverlay
            hint={activeHint.text}
            type={activeHint.type}
            onContinue={dismissActiveHint}
            isPaused={isUserPaused}
          />
        )}

        {/* Hint Toast */}
        {activeHint && activeHint.type !== 'warning' && (
          <GuidedHintToast
            hint={activeHint.text}
            type={activeHint.type}
            onClose={dismissActiveHint}
            duration={activeHint.type === 'praise' ? 3000 : 10000}
          />
        )}

        {/* Success Toast */}
        {successHint && (
          <HintSuccessToast
            hint={successHint}
            onClose={dismissSuccessHint}
            duration={4000}
          />
        )}
        
        {/* Microphone Fallback Toast */}
        {showMicFallbackToast && (
          <InfoToast
            message={t('scenario.session.keyboard.microphoneFallback')}
            icon={<span className="text-sm">⌨️</span>}
            onClose={() => setShowMicFallbackToast(false)}
            duration={8000}
          />
        )}

        {/* Detailed Help Overlay */}
        {currentHelp && !websocketSession.state.aiResponseInProgress && (
          <HintPauseOverlay
            hint={currentHelp.text}
            type="detailed_help"
            onContinue={dismissHelp}
            isPaused={isUserPaused}
          />
        )}

        {/* Invalid Transcript Overlay */}
        {isTranscriptInvalid && (
          <InvalidTranscriptOverlay 
            reason={invalidReason}
            onRestart={handleTranscriptInvalidRestart}
          />
        )}

        {/* Manual Pause Overlay */}
        {isUserPaused && (
          <PauseOverlay
            onResume={handleResume}
            onSave={onSave ? handleSaveFromPause : undefined}
            isSaving={isSaving}
          />
        )}
      </div>

      {/* Keyboard Input */}
      {sessionStatus === 'active' && websocketSession.state.inputMode === 'keyboard' && (
        <KeyboardInput
          onSendMessage={websocketSession.sendTextMessage}
          disabled={isUserPaused || waitingForAIGreeting}
          isAIResponding={websocketSession.state.aiResponseInProgress !== ''}
          onMessageSubmit={() => {
            if (websocketSession.state.aiResponseInProgress) {
              logger.debug('[Keyboard Mode] User submitted message - interrupting AI response');
              websocketSession.cancelResponse?.();
            }
          }}
        />
      )}
    </div>

    {/* Transcript Debug Button (disabled for production) */}
    {false && sessionStatus === 'active' && !showTranscriptModal && (
      <div 
        className="md:top-4"
        style={{
          position: 'fixed',
          right: '1rem',
          top: (sessionData?.scenario as any)?.userReferenceInfo ? '5rem' : '106px',
          zIndex: 9999
        }}>
        <button
          onClick={() => setShowTranscriptModal(true)}
          style={{
            backgroundColor: '#9333ea',
            color: 'white',
            padding: '0.75rem',
            borderRadius: '9999px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            border: 'none',
            transition: 'all 0.3s ease'
          }}
          title="View Transcript Debug"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </button>
      </div>
    )}

    {/* User Reference Info Button */}
    {(sessionData?.scenario as any)?.userReferenceInfo && sessionStatus === 'active' && !showReferenceModal && (
      <div 
        className="md:top-4 top-[106px]"
        style={{
          position: 'fixed',
          right: '1rem',
          zIndex: 9999
        }}>
        {!hasClickedReferenceButton && shouldShowPulse() && (
          <>
            <span className="pulse-ring" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: '9999px',
              backgroundColor: 'rgba(74, 222, 128, 0.5)',
              pointerEvents: 'none'
            }}></span>
            <span className="pulse-ring" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: '9999px',
              backgroundColor: 'rgba(74, 222, 128, 0.5)',
              pointerEvents: 'none',
              animationDelay: '1s'
            }}></span>
          </>
        )}
        
        <button
          onClick={() => {
            setShowReferenceModal(true);
            setHasClickedReferenceButton(true);
          }}
          style={{
            position: 'relative',
            backgroundColor: '#16a34a',
            color: 'white',
            padding: hasClickedReferenceButton ? '0.75rem' : '0.75rem 1rem',
            borderRadius: '9999px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            border: 'none',
            transition: 'all 0.3s ease'
          }}
          title={t('scenario.session.referenceInfo.title', 'Reference Information')}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {!hasClickedReferenceButton && (
            <span style={{ fontWeight: 600 }}>
              {t('scenario.session.referenceInfo.buttonLabel', 'Info')}
            </span>
          )}
          {!hasClickedReferenceButton && (
            <span style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              width: '20px',
              height: '20px',
              backgroundColor: '#ef4444',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>!</span>
          )}
        </button>
      </div>
    )}

    {/* 🔙 Exit confirmation dialog (triggered by browser back button or external control) */}
    {showExitConfirmation && (
      <ExitConfirmDialog
        onSave={onSave ? handleExitSave : async () => { handleExitWithoutSaving(); }}
        onExitWithoutSaving={handleExitWithoutSaving}
        onCancel={handleExitCancel}
        isSaving={isSaving}
      />
    )}

    {/* 📞 Hangup confirmation dialog (triggered by red end-call button) */}
    {showHangupConfirm && (
      <HangupConfirmDialog
        onEndAndEvaluate={handleHangupEndAndEvaluate}
        onSaveAndExit={onSave ? handleHangupSaveAndExit : undefined}
        onCancel={handleHangupCancel}
        isSaving={isSaving}
        isGameMode={sessionData?.scenario?.isGameMode}
      />
    )}

    </>
  );
}
